import React, { useId } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { SALES_COLORS } from './theme';
import { formatAxisLabel, formatBucketLabel, formatCount, formatCurrency, formatPercent, netCents, netSalesCount } from './salesUtils';

export const CHART_HEIGHT = 'h-56 sm:h-64';

const TipRow = ({ label, value, valueClass = 'text-gray-200' }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="text-gray-500">{label}</span>
    <span className={`sales-num ${valueClass}`}>{value}</span>
  </div>
);

const ChartTooltip = ({ active, payload, currency, granularity }) => {
  if (!active || !payload || !payload.length) return null;
  const b = payload[0].payload;
  return (
    <div className="sales-tooltip">
      <p className="sales-label text-gray-400 mb-1.5">{formatBucketLabel(b.bucket_start, granularity)}</p>
      <p className="flex items-center gap-2 mb-2">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: SALES_COLORS.accent }} aria-hidden="true" />
        <span className="font-display font-semibold text-base text-white sales-num">{formatCurrency(netCents(b), currency)}</span>
        <span className="text-gray-500 sales-num">net &middot; {formatCount(netSalesCount(b))} sales</span>
      </p>
      <div className="space-y-0.5 pt-2 border-t border-white/5">
        <TipRow label="Gross" value={formatCurrency(b.gross_cents, currency)} />
        <TipRow
          label="Refunded"
          value={formatCurrency(b.refunded_cents, currency)}
          valueClass={(b.refunded_cents || 0) > 0 ? 'text-battle-red-light/80' : 'text-gray-200'}
        />
      </div>
      <div className="space-y-0.5 pt-2 mt-2 border-t border-white/5">
        <TipRow label="Of this video's range" value={formatPercent(b.pct_of_range_revenue)} />
        <TipRow label="Of all sales this period" value={formatPercent(b.pct_of_bucket_revenue)} />
      </div>
    </div>
  );
};

const TICK = { fill: SALES_COLORS.axis, fontSize: 11, fontFamily: 'Inter, sans-serif' };

// Net revenue (gross minus refunds) per bucket for this video.
const SalesChart = ({ series, currency, granularity }) => {
  const gradientId = `sales-area-${useId().replace(/:/g, '')}`;
  const data = (series || []).map((b) => ({ ...b, revenue: netCents(b) / 100 }));
  return (
    <div className="sales-card h-full" data-testid="sales-chart">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="sales-heading">Net revenue over time</h3>
        <span className="sales-label">per {granularity}</span>
      </div>
      {/* Gradient lives in its own zero-size svg (referenced by id) so it is always
          rendered in an SVG context, whatever recharts does with unsized containers. */}
      <svg width="0" height="0" className="absolute" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SALES_COLORS.accent} stopOpacity={0.32} />
            <stop offset="100%" stopColor={SALES_COLORS.accent} stopOpacity={0} />
          </linearGradient>
        </defs>
      </svg>
      <div className={CHART_HEIGHT}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 4, left: -4, bottom: 0 }}>
            <CartesianGrid stroke={SALES_COLORS.grid} strokeDasharray="2 6" vertical={false} />
            <XAxis
              dataKey="bucket_start"
              tickFormatter={(v) => formatAxisLabel(v, granularity)}
              tick={TICK}
              axisLine={{ stroke: SALES_COLORS.grid }}
              tickLine={false}
              tickMargin={8}
              minTickGap={24}
            />
            <YAxis
              tickFormatter={(v) => formatCurrency(v * 100, currency).replace(/\.00$/, '')}
              tick={TICK}
              axisLine={false}
              tickLine={false}
              width={56}
              allowDecimals={false}
            />
            <Tooltip
              content={<ChartTooltip currency={currency} granularity={granularity} />}
              cursor={{ stroke: SALES_COLORS.accent, strokeOpacity: 0.35, strokeDasharray: '3 3' }}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke={SALES_COLORS.accent}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4.5, fill: SALES_COLORS.accent, stroke: SALES_COLORS.surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SalesChart;
